import type { LoaderFunctionArgs } from '~/types/route-args';
import type { ShouldRevalidateFunction } from 'react-router';
import type { DirectoryEntityType } from '~/ui-rocco/pages/directory-browse/DirectoryBrowsePage';
import type { SearchServiceSortOption } from '~/modules/search/type/api';
import { validateSort } from '~/modules/search/util/apply-es-search-sort';
import { getDirectoryGridPageByOffset } from '@modules/category/util/get-directory-browse-data.server';
import type { AppLoadContext } from 'react-router';
import {
    hasEnoughPinnedSuppliers,
    interleaveByProfessional,
    isDefaultProductListEnabled,
    PRODUCTS_ROOT_PATH,
    stripTrailingSlash,
} from '@modules/category/util/default-product-list.server';
import { getFeaturedBrandPinsCached } from '@modules-rocco/professionals/api/get-featured-brand-pins.server';
import { getProductSuppliersPageByOffset } from '@modules/category/util/get-product-suppliers-page.server';
import { isProductSuppliersPage } from '~/modules/url/utils/product-suppliers';
import { resolveImageLabelView } from '@modules-rocco/projects/config/project-browse-views';
import {
    getImageLabelGridPageByOffset,
    parseImageLabelFilters,
} from '@modules-rocco/projects/api/get-image-label-browse-data.server';
import {
    categoryGateEnabledForLink,
    resolveContentGate,
} from '~/modules-rocco/content-gate/resolve-content-gate.server';

interface ResolveDefaultProductListArgs {
    context: AppLoadContext;
    entityType: DirectoryEntityType;
    categoryLink: string;
    filters?: Record<string, unknown>;
}

/**
 * Returns the supplier filter to apply when this request is a further page of
 * the curated products root, or undefined when the ordinary catalogue applies.
 * Any user-applied filter hands the page back to the full catalogue, matching
 * the page loader's gate.
 */
const resolveDefaultProductList = async ({
    context,
    entityType,
    categoryLink,
    filters,
}: ResolveDefaultProductListArgs): Promise<
    { filters: Record<string, unknown> } | undefined
> => {
    if (
        entityType !== 'products' ||
        stripTrailingSlash(categoryLink) !== PRODUCTS_ROOT_PATH
    ) {
        return undefined;
    }

    if (filters && Object.keys(filters).length > 0) {
        return undefined;
    }

    if (!isDefaultProductListEnabled(context)) {
        return undefined;
    }

    const pinnedSuppliers = (await getFeaturedBrandPinsCached(context)).map(
        (brand) => brand.urlSegment,
    );

    return hasEnoughPinnedSuppliers(pinnedSuppliers)
        ? { filters: { professionals: pinnedSuppliers } }
        : undefined;
};

/** Resource-route path for the directory browse View More (all entity types). */
export const LOAD_MORE_DIRECTORY_ACTION = '/actions/load-more-directory';

/**
 * What a page that could not be read answers with. A 200 carrying `error`,
 * not an error status: the grid loads pages through `fetcher.load`, and a
 * failed fetcher lands in the route's error boundary, replacing a wall of
 * tiles the reader was scrolling with an error page because one more page
 * was late. The search gateway fails this way in bursts (502s under load),
 * which the grid rides out by retrying. `hasMore` stays true so the page is
 * still on offer; `totalCount` is unknown and the grid keeps its own.
 */
const failedPage = (error: string) => ({
    tiles: [],
    hasMore: true,
    totalCount: 0,
    error,
});

/** Max tiles per request — guards against manipulated `limit` values. */
const MAX_LIMIT = 100;

const ENTITY_TYPES: DirectoryEntityType[] = [
    'products',
    'professionals',
    'projects',
];

const parseEntityType = (value: string | null): DirectoryEntityType =>
    ENTITY_TYPES.includes(value as DirectoryEntityType)
        ? (value as DirectoryEntityType)
        : 'products';

/** `/products`, `/professionals`, `/projects` roots per entity. */
const entityBase = (entityType: DirectoryEntityType): string =>
    `/${entityType}`;

/**
 * Validate the `sort` param to a Search Service sort option. Filter-style sorts
 * (`uncategorised` / `invalidLink`) aren't valid grid orderings, so they resolve
 * to undefined and the caller falls back to the entity default.
 */
const parseSort = (
    value: string | null,
): SearchServiceSortOption | undefined => {
    const sort = validateSort(value ?? undefined);
    if (!sort || sort === 'uncategorised' || sort === 'invalidLink') {
        return undefined;
    }
    return sort;
};

/**
 * Fetch the next page of the flat directory browse grid (offset/limit + filters)
 * for the View More hook. Entity-agnostic: dispatches to the right search index
 * by `entityType`.
 *
 * Query params: `entityType`, `categoryLink`, `offset`, `limit`, `filters` (JSON).
 * Security: validates `categoryLink` is a same-directory path (no traversal).
 */
export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);

    const entityType = parseEntityType(url.searchParams.get('entityType'));
    const categoryLink = url.searchParams.get('categoryLink') ?? '';
    const offsetParam = url.searchParams.get('offset');
    const limitParam = url.searchParams.get('limit');
    const filtersParam = url.searchParams.get('filters');
    // The in-listing keyword term, repeated so later pages stay inside the
    // same result set the first page came from.
    const search = url.searchParams.get('search')?.trim() || undefined;
    const sortBy = parseSort(url.searchParams.get('sort'));

    const base = entityBase(entityType);
    const categoryLinkPattern = new RegExp(`^${base}(/[\\w-]+)*$`);

    if (
        !categoryLink.startsWith(base) ||
        categoryLink.includes('..') ||
        !categoryLinkPattern.test(categoryLink)
    ) {
        logger.warning('Invalid categoryLink in load-more-directory', {
            entityType,
            categoryLink,
        });
        return { tiles: [], hasMore: false, totalCount: 0 };
    }

    const offset = offsetParam ? parseInt(offsetParam, 10) : 0;
    const limit = limitParam ? parseInt(limitParam, 10) : 36;
    if (
        !Number.isInteger(offset) ||
        offset < 0 ||
        !Number.isInteger(limit) ||
        limit < 1 ||
        limit > MAX_LIMIT
    ) {
        logger.warning('Invalid offset/limit in load-more-directory', {
            offsetParam,
            limitParam,
        });
        return { tiles: [], hasMore: false, totalCount: 0 };
    }

    // The gate is enforced here as well as in the loader: the grid stops offering
    // View More once it closes, but the endpoint behind it is a plain URL and must
    // refuse on its own rather than trust the UI that called it. The Brands view
    // is a brand listing rather than the catalogue, so it stays open.
    const isSuppliersLink =
        entityType === 'products' && isProductSuppliersPage(categoryLink);
    if (!isSuppliersLink) {
        const contentGate = await resolveContentGate({
            context,
            request,
            surface: entityType,
            categoryEnabled: categoryGateEnabledForLink(
                context,
                request,
                categoryLink,
            ),
        });
        if (contentGate.gated) {
            return { tiles: [], hasMore: false, totalCount: 0 };
        }
    }

    let filters: Record<string, unknown> | undefined;
    if (filtersParam) {
        try {
            const parsed = JSON.parse(filtersParam);
            if (typeof parsed === 'object' && parsed !== null) {
                filters = parsed;
            }
        } catch {
            logger.warning('Invalid filters JSON in load-more-directory', {
                filtersParam,
            });
        }
    }

    // Spaces / Styles (/projects/spaces, /projects/styles) are views over the
    // ImageLabel index rather than entity types of their own — as with
    // /products/suppliers below, the trailing segment is what selects them. The
    // facet is re-read from the payload but the rest of the query is rebuilt
    // from context, so a tampered `filters` cannot widen the search.
    const imageLabelView =
        entityType === 'projects'
            ? resolveImageLabelView(categoryLink)
            : undefined;
    if (imageLabelView) {
        try {
            const { facets, search, uniqueImage } =
                parseImageLabelFilters(filters);
            const { tiles, hasMore, totalCount, rowCount } =
                await getImageLabelGridPageByOffset({
                    context,
                    facets,
                    search,
                    offset,
                    limit,
                    sortBy,
                    view: imageLabelView,
                    uniqueImage,
                });
            return {
                tiles: tiles.map((model) => ({ model })),
                hasMore,
                totalCount,
                // Where the read got to in the index: the rows it actually
                // consumed, which is what `hasMore` is measured against too.
                // The wall collapses a photo labelled more than once into one
                // tile, so the tiles it shows run behind those rows — paging on
                // the tile count would ask for rows it already has, and paging
                // on the page size would skip the rows a short page never
                // returned.
                nextOffset: offset + rowCount,
            };
        } catch (error) {
            logger.error('Error loading more project image labels', {
                error,
                categoryLink,
                offset,
                limit,
            });
            return failedPage('Search Service request failed');
        }
    }

    // "Browse by Brands" (/products/suppliers) is a view over the product
    // index, not an entity type of its own — the `/suppliers` segment in the
    // link is what selects it. It needs the aggregate-then-search pass rather
    // than a single index read.
    const isSuppliers = isSuppliersLink;
    if (isSuppliers) {
        try {
            const { tiles, hasMore, totalCount } =
                await getProductSuppliersPageByOffset({
                    context,
                    categoryLink,
                    offset,
                    limit,
                    filters,
                    search,
                    sortBy,
                    signal: request.signal,
                });
            return {
                tiles: tiles.map((model) => ({ model })),
                hasMore,
                totalCount,
            };
        } catch (error) {
            logger.error('Error loading more product suppliers', {
                error,
                categoryLink,
                offset,
                limit,
            });
            return failedPage('Search Service request failed');
        }
    }

    // The curated products root narrows and reorders the grid server-side, so
    // later pages have to repeat that work — the client sends no filters for it
    // (deliberately, so nothing shows up as an applied filter in the UI).
    const curated = await resolveDefaultProductList({
        context,
        entityType,
        categoryLink,
        filters,
    });

    try {
        const { tiles, hasMore, totalCount } =
            await getDirectoryGridPageByOffset({
                context,
                entityType,
                categoryLink,
                offset,
                limit,
                filters: curated?.filters ?? filters,
                search,
                sortBy,
                hitsTransform: curated ? interleaveByProfessional : undefined,
                signal: request.signal,
            });

        // Wrap raw models as tile view models; the caller stamps the aspect ratio.
        return {
            tiles: tiles.map((model) => ({ model })),
            hasMore,
            totalCount,
        };
    } catch (error) {
        logger.error('Error in /actions/load-more-directory', {
            error,
            entityType,
            categoryLink,
            offset,
            limit,
        });
        return failedPage('Search Service request failed');
    }
};

/** Never revalidate — this endpoint only serves explicit `fetcher.load` calls. */
export const shouldRevalidate: ShouldRevalidateFunction = () => false;
